import React, { useState } from 'react';
import { Workflow, Container, Scale } from 'lucide-react';

import EnterpriseSolutionModal from '../components/EnterpriseSolutionModal';
import PageMasthead from '../components/PageMasthead';
import SiteFooter from '../components/SiteFooter';
import SiteHeader from '../components/SiteHeader';

/**
 * CR3ATE RECONSTRUCTION SDK — the engine as a container you run yourself.
 *
 * Same shape as /desktop: masthead and pitch left, the Intergeo offer boxed
 * right, three capabilities in a band beneath, the runs-on line last.
 *
 * Cut hard from the source copy, which ran to three sections and a separate
 * how-it-works. A developer deciding whether to ask for access needs three
 * things — what it exposes, what it runs on, what it costs to try — and the
 * pipeline-stage enumeration belongs in the docs they get afterwards, not on the
 * page that decides whether they ask.
 */

const CAPABILITIES = [
  {
    n: '01',
    label: 'Control with convenience',
    Icon: Workflow,
    body: 'As automated or as controlled as you wish. Covers ingestion, '
        + 'calibration, features, pose, georeferencing, depth, mesh and Gaussian '
        + 'training — end to end, restartable and programmable.',
  },
  {
    n: '02',
    label: 'Easy to get started',
    Icon: Container,
    body: 'A Docker image running on Linux with the NVIDIA Container Toolkit. '
        + 'Full-featured Python SDK with extensive documentation, and support '
        + 'options to help you deploy, tune and implement.',
  },
  {
    n: '03',
    label: 'Flexible to your needs',
    Icon: Scale,
    body: 'Single GPU, multi-GPU, distributed nodes and datacentre hardware, all '
        + 'in the same pipeline. No network, intermittent or hyper-connected — all '
        + 'fair game. Scales from a single room to entire city blocks.',
  },
] as const;

const Cr3ateSdkPage: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="flex min-h-dvh flex-col bg-ink-950 text-ink-100">
      <SiteHeader />

      <main className="relative flex-1">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-96 bg-[radial-gradient(60%_60%_at_50%_0%,rgba(12,180,228,0.09),transparent_70%)]"
        />

        <div className="relative mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:py-10">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.15fr_1fr] lg:items-center lg:gap-12">
            <div>
              <PageMasthead
                title="Cr3ate Reconstruction SDK"
                tagline="The engine behind every Cr3ate model, in a Python SDK."
              />
              <p className="mt-6 text-balance font-brand text-xl font-light leading-snug tracking-tight text-ink-100 sm:text-2xl">
                Our pipeline.
                <br />
                Your code.
              </p>
              <p className="mt-4 max-w-xl text-balance font-brand text-body font-light leading-relaxed text-ink-300">
                Granular control at every stage of the pipeline. Complete data
                privacy. Responsive support.
              </p>
            </div>

            <div className="rounded-2xl border border-ember-500/40 bg-ember-500/[0.06] p-6 sm:p-7">
              <p className="mt-3 text-balance font-brand text-3xl font-light tracking-tight text-ink-100 sm:text-4xl">
                Start your SDK trial.
              </p>
              <p className="mt-3 text-body font-light text-ink-200">
                Reach out for special licensing rates.
              </p>
              <p className="mt-1 text-ui font-light text-ink-400">
                Tell us what you are building and we will send a container and a key.
              </p>
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className="mt-5 inline-flex w-full items-center justify-center rounded-lg bg-crane-500 px-6 py-3 font-brand text-body font-medium text-ink-950 transition-colors hover:bg-crane-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-400 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-950 sm:w-auto"
              >
                Request SDK access
              </button>
            </div>
          </div>

          <div className="mt-6 border-t border-ink-800 pt-5 lg:mt-8">
            <h2 className="font-brand text-meta font-medium uppercase tracking-[0.18em] text-ember-500">
              What you get
            </h2>

            <div className="mt-5 grid grid-cols-1 gap-x-10 gap-y-6 sm:grid-cols-3">
              {CAPABILITIES.map(({ n, label, body, Icon }) => (
                <div key={n}>
                  <div className="flex items-center gap-2.5">
                    <Icon className="h-5 w-5 flex-shrink-0 text-ember-500" aria-hidden />
                    <span className="font-brand text-meta font-medium uppercase tracking-[0.16em] text-ink-400">
                      {n}
                    </span>
                    <span className="font-brand text-title font-medium tracking-wide text-ink-100">
                      {label}
                    </span>
                  </div>
                  <p className="mt-2.5 text-ui font-light leading-relaxed text-ink-300">
                    {body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />

      <EnterpriseSolutionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Request SDK Access"
        subtitle="Tell us what you are building and we will send a container and a key"
        source="sdk"
      />
    </div>
  );
};

export default Cr3ateSdkPage;
